'use client'

import { useEffect } from 'react'

import { CONTACT_EMAIL, SITE_URL } from '../lib/agents'
import { PROJECTS } from '../lib/projects'
import {
  PORTFOLIO_CASE_STUDIES,
  PORTFOLIO_COMMITS,
  PORTFOLIO_PROJECTS_SHIPPED,
  PORTFOLIO_STACK,
  PORTFOLIO_YEARS,
} from '../lib/portfolio-stats'

type WebMCPTool = {
  name: string
  description: string
  inputSchema: Record<string, unknown>
  execute: (args: Record<string, unknown>) => Promise<{ content: Array<{ type: 'text'; text: string }> }>
}

type ModelContext = {
  registerTool?: (tool: WebMCPTool) => void
  provideContext?: (context: { tools: WebMCPTool[] }) => void
}

const EMPTY_SCHEMA = { type: 'object', properties: {} }

function text(value: unknown) {
  return {
    content: [
      { type: 'text' as const, text: typeof value === 'string' ? value : JSON.stringify(value, null, 2) },
    ],
  }
}

const TOOLS: WebMCPTool[] = [
  {
    name: 'list_projects',
    description: 'List Aries Spring portfolio case studies with name, discipline, and description.',
    inputSchema: EMPTY_SCHEMA,
    execute: async () =>
      text(PROJECTS.map(({ name, discipline, description }) => ({ name, discipline, description }))),
  },
  {
    name: 'get_stats',
    description: 'Get portfolio stats: years of experience, projects shipped, commits, and tech stack.',
    inputSchema: EMPTY_SCHEMA,
    execute: async () =>
      text({
        yearsOfExperience: PORTFOLIO_YEARS,
        projectsShipped: PORTFOLIO_PROJECTS_SHIPPED,
        commits: PORTFOLIO_COMMITS,
        caseStudies: PORTFOLIO_CASE_STUDIES,
        stack: PORTFOLIO_STACK,
      }),
  },
  {
    name: 'get_contact',
    description: 'Get contact details for hiring or collaborating with Aries Spring.',
    inputSchema: EMPTY_SCHEMA,
    execute: async () =>
      text({ email: CONTACT_EMAIL, website: SITE_URL, quoteConcierge: `${SITE_URL}/#quote` }),
  },
  {
    name: 'open_section',
    description: 'Scroll the page to a named section: craft, proof, work, terminal, quote, or contact.',
    inputSchema: {
      type: 'object',
      properties: {
        section: {
          type: 'string',
          enum: ['craft', 'proof', 'work', 'terminal', 'quote', 'contact'],
          description: 'Section anchor to scroll to',
        },
      },
      required: ['section'],
    },
    execute: async (args) => {
      const section = String(args.section ?? '')
      const target = document.getElementById(section)
      if (!target) return text(`Unknown section: ${section}`)
      target.scrollIntoView({ behavior: 'smooth' })
      return text(`Scrolled to #${section}`)
    },
  },
]

export default function WebMCPTools() {
  useEffect(() => {
    const modelContext = (navigator as Navigator & { modelContext?: ModelContext }).modelContext
    if (!modelContext) return

    try {
      if (typeof modelContext.registerTool === 'function') {
        for (const tool of TOOLS) modelContext.registerTool(tool)
      } else if (typeof modelContext.provideContext === 'function') {
        modelContext.provideContext({ tools: TOOLS })
      }
    } catch {
      // WebMCP is experimental; never let registration break the page.
    }
  }, [])

  return null
}
